import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Nav,
  Tab,
  Badge,
  Modal,
  Spinner,
  Alert,
  Toast,
  ToastContainer,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import {
  FaSearch,
  FaSync,
  FaEnvelope,
  FaTrash,
  FaFilter,
  FaUsers,
  FaComments,
  FaCheckDouble,
} from 'react-icons/fa';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '../../contexts/ThemeContext';
import { adminDiscussionAPI } from '../../services/api';
import DiscussionList from './DiscussionList';
import DiscussionDetail from './DiscussionDetail';
import DiscussionStats from './DiscussionStats';
import './AdminDiscussions.css';

const AdminDiscussions = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { isDark } = useTheme();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    hasUnread: false,
    dateFrom: '',
    dateTo: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discussionToDelete, setDiscussionToDelete] = useState(null);

  // Check admin access
  useEffect(() => {
    if (!user?.is_staff && !user?.is_superuser && user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch discussions
  const fetchDiscussions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filters.hasUnread) params.has_unread = 'true';
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await adminDiscussionAPI.getAllDiscussions(params);
      // Handle paginated response structure
      const discussionsData = response.data?.data;
      if (Array.isArray(discussionsData)) {
        setDiscussions(discussionsData);
      } else if (discussionsData?.data && Array.isArray(discussionsData.data)) {
        setDiscussions(discussionsData.data);
      } else {
        setDiscussions([]);
      }

      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des discussions');
      console.error('Error fetching discussions:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await adminDiscussionAPI.getStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchDiscussions();
    fetchStats();
  }, [fetchDiscussions, fetchStats]);

  // Handlers
  const handleDiscussionSelect = (discussion) => {
    setSelectedDiscussion(discussion);
    setActiveTab(2); // Switch to detail view
  };

  const handleSendMessage = async (message, asUserId) => {
    if (!selectedDiscussion || !message.trim() || !asUserId) return;

    try {
      await adminDiscussionAPI.sendMessage(selectedDiscussion.id, {
        message,
        as_user_id: asUserId,
      });

      setSnackbar({
        open: true,
        message: 'Message envoyé avec succès',
        severity: 'success',
      });

      fetchStats(); // Refresh stats
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'envoi du message',
        severity: 'error',
      });
    }
  };

  const handleDeleteDiscussion = async () => {
    if (!discussionToDelete) return;

    try {
      await adminDiscussionAPI.deleteDiscussion(discussionToDelete.id);
      setDiscussions(prev => prev.filter(d => d.id !== discussionToDelete.id));
      if (selectedDiscussion?.id === discussionToDelete.id) {
        setSelectedDiscussion(null);
      }
      setSnackbar({
        open: true,
        message: 'Discussion supprimée avec succès',
        severity: 'success',
      });
      fetchStats();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setDiscussionToDelete(null);
    }
  };

  const handleMarkAsRead = async (discussionId, userId) => {
    try {
      await adminDiscussionAPI.markAsRead(discussionId, { user_id: userId });
      setSnackbar({
        open: true,
        message: 'Messages marqués comme lus',
        severity: 'success',
      });
      fetchDiscussions();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du marquage',
        severity: 'error',
      });
    }
  };

  const handleRefresh = () => {
    fetchDiscussions();
    fetchStats();
  };

  // Render
  return (
    <Container fluid className={`admin-discussions-page py-4 ${isDark ? 'dark' : ''}`} style={{ maxWidth: 1600 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <h2 className="fw-bold mb-0">Gestion des Discussions</h2>
        <Button
          variant="outline-secondary"
          onClick={handleRefresh}
          disabled={loading}
        >
          <FaSync className="me-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6} md={3}>
            <Card className="admin-stats-card h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <FaComments size={32} className="text-primary" />
                </div>
                <div>
                  <h3 className="mb-0">{stats.total_conversations}</h3>
                  <small className="text-muted">Total Discussions</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Card className="admin-stats-card h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <FaEnvelope size={32} className="text-info" />
                </div>
                <div>
                  <h3 className="mb-0">{stats.total_messages}</h3>
                  <small className="text-muted">Total Messages</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Card className="admin-stats-card h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FaCheckDouble size={32} className="text-warning" />
                </div>
                <div>
                  <h3 className="mb-0">{stats.unread_messages}</h3>
                  <small className="text-muted">Messages Non Lus</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Card className="admin-stats-card h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaUsers size={32} className="text-success" />
                </div>
                <div>
                  <h3 className="mb-0">{stats.active_conversations}</h3>
                  <small className="text-muted">Discussions Actives</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Main Content */}
      <Card className="admin-discussions-main-card shadow-sm" style={{ minHeight: 600 }}>
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(parseInt(k))}>
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey={0}>Liste des Discussions</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={1}>Statistiques</Nav.Link>
              </Nav.Item>
              {selectedDiscussion && (
                <Nav.Item>
                  <Nav.Link eventKey={2}>Détail de la Discussion</Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </Card.Header>

          {/* Search and Filters */}
          {activeTab !== 1 && (
            <Card.Body className="admin-discussions-filters border-bottom">
              <div className="d-flex gap-3 flex-wrap align-items-center">
                <InputGroup className="flex-grow-1" style={{ minWidth: 250 }}>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <FormControl
                    placeholder="Rechercher par nom d'utilisateur..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchDiscussions()}
                  />
                </InputGroup>
                <Button
                  variant={showFilters ? 'primary' : 'outline-secondary'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter className="me-2" />
                  Filtres
                </Button>
              </div>

              {showFilters && (
                <div className="d-flex gap-3 flex-wrap mt-3">
                  <Form.Select
                    style={{ minWidth: 150 }}
                    value={filters.hasUnread ? 'unread' : 'all'}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      hasUnread: e.target.value === 'unread'
                    }))}
                  >
                    <option value="all">Toutes</option>
                    <option value="unread">Non lues</option>
                  </Form.Select>
                  <FormControl
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  />
                  <FormControl
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  />
                  <Button variant="outline-secondary" onClick={() => {
                    setFilters({ hasUnread: false, dateFrom: '', dateTo: '' });
                    setSearchQuery('');
                  }}>
                    Réinitialiser
                  </Button>
                </div>
              )}
            </Card.Body>
          )}

          {/* Tab Content */}
          <Tab.Content>
            <Tab.Pane eventKey={0}>
              <DiscussionList
                discussions={discussions}
                loading={loading}
                onSelect={handleDiscussionSelect}
                onDelete={(discussion) => {
                  setDiscussionToDelete(discussion);
                  setDeleteDialogOpen(true);
                }}
                onMarkAsRead={handleMarkAsRead}
                selectedId={selectedDiscussion?.id}
              />
            </Tab.Pane>

            <Tab.Pane eventKey={1}>
              <DiscussionStats stats={stats} />
            </Tab.Pane>

            <Tab.Pane eventKey={2}>
              {selectedDiscussion && (
                <DiscussionDetail
                  discussion={selectedDiscussion}
                  onSendMessage={handleSendMessage}
                  onBack={() => setActiveTab(0)}
                />
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteDialogOpen} onHide={() => setDeleteDialogOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer cette discussion ? Cette action est irréversible.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDeleteDiscussion}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="admin-toast p-3">
        <Toast
          show={snackbar.open}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          delay={6000}
          autohide
          bg={snackbar.severity}
        >
          <Toast.Body className="text-white">{snackbar.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default AdminDiscussions;
